import React, { useState, useEffect, useCallback } from 'react';
import { Package, Save, Loader2, Plus } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import { AssetCategoryModal } from './AssetCategoryModal';

import { useToast } from '../hooks/useToast';
import { client, fetchOrganizations, fetchStoresWhereStaffAssigned, fetchAssetCategories, fetchVendors } from '../apiHelper';

export const FixedAssetRegistration = ({ currentUser }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [organizations, setOrganizations] = useState([]);
    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);

    const [formObject, setFormObject] = useState({
        organization_id: '',
        assetcategory_id: '',
        store_id: '',
        vendor_id: '',
        asset_number: '',
        name: '',
        description: '',
        in_service_date: new Date().toISOString().split('T')[0],
        purchase_cost: '',
        salvage_value: '0.00',
        accumulated_depreciation: 0.00,
        useful_life_months: '',
        depreciation_method: 'STRAIGHT_LINE'
    });

    const loadCategories = useCallback(async () => {
        try {
            const categoryList = await fetchAssetCategories();
            const cats = Array.isArray(categoryList) ? categoryList : [];
            setCategories(cats.map(c => ({
                label: `${c.code} - ${c.name}`,
                value: c.assetcategory_id.toString(),
                categoryData: c
            })));
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to reload asset categories.", "error");
        }
    }, [showToast]);

    const loadMasterData = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const [orgs, storeList, vendorList] = await Promise.all([
                fetchOrganizations().catch(() => []),
                fetchStoresWhereStaffAssigned(currentUser).catch(() => []),
                // client.fetchData('/fixed-assets/categories').catch(() => []),
                // client.fetchData('/vendors').catch(() => [])
                fetchVendors(currentUser).catch(() => []),
            ]);

            const processedOrgs = orgs.map(o => ({ label: o.organization_name, value: o.organization_id.toString() }))
            const myOrg = processedOrgs.find(o => o.value === currentUser.org_id);
            setOrganizations([myOrg]);
            setStores(storeList.map(s => ({ label: s.identifier || `Store ${s.store_id}`, value: s.store_id.toString() })));

            // const cats = Array.isArray(categoryList) ? categoryList : [];
            // setCategories(cats.map(c => ({
            //     label: `${c.code} - ${c.name}`,
            //     value: c.assetcategory_id.toString(),
            //     categoryData: c
            // })));

            const vns = Array.isArray(vendorList) ? vendorList : [];
            setVendors(vns.map(v => ({ label: v.vendorname, value: v.vendor_id.toString() })));

            await loadCategories();
        } catch (err) {
            showToast(err.response?.data?.detail || "Error retrieving asset reference data.", "error");
        } finally {
            setLoading(false);
        }
    }, [currentUser, loadCategories, showToast]);

    useEffect(() => {
        loadMasterData();
    }, [loadMasterData]);

    const handleCategoryChange = (e) => {
        const catId = e.target.value;
        const selected = categories.find(c => c.value === catId);

        setFormObject(prev => ({
            ...prev,
            assetcategory_id: catId,
            useful_life_months: selected?.categoryData?.default_useful_life_months || prev.useful_life_months,
            depreciation_method: selected?.categoryData?.depreciation_method || prev.depreciation_method
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormObject(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formObject.organization_id || !formObject.assetcategory_id || !formObject.asset_number || !formObject.purchase_cost) {
            showToast("Organization, Category, Tag Number, and Cost are required.", "error");
            return;
        }

        setSubmitting(true);
        try {
            const cost = parseFloat(formObject.purchase_cost);
            const salvage = parseFloat(formObject.salvage_value || 0);
            const priorAccumDepr = parseFloat(formObject.accumulated_depreciation || 0);
            const currentBookValue = cost - priorAccumDepr;

            const payload = {
                organization_id: parseInt(formObject.organization_id, 10),
                assetcategory_id: parseInt(formObject.assetcategory_id, 10),
                store_id: formObject.store_id ? parseInt(formObject.store_id, 10) : null,
                vendor_id: formObject.vendor_id ? parseInt(formObject.vendor_id, 10) : null,
                asset_number: formObject.asset_number.trim(),
                name: formObject.name.trim(),
                description: formObject.description || null,
                in_service_date: formObject.in_service_date,
                purchase_cost: cost,
                salvage_value: salvage,
                useful_life_months: parseInt(formObject.useful_life_months, 10),
                depreciation_method: formObject.depreciation_method,
                accumulated_depreciation: priorAccumDepr,
                current_book_value: currentBookValue > 0 ? currentBookValue : 0.00,
                status: "ACTIVE"
            };

            console.log(payload)

            const response = await client.postData("/accounting/capitalize-fixed-asset", payload);
            showToast(response.msg || "Fixed Asset capitalized successfully.", "success");
            setFormObject({
                organization_id: '',
                assetcategory_id: '',
                store_id: '',
                vendor_id: '',
                asset_number: '',
                name: '',
                description: '',
                in_service_date: new Date().toISOString().split('T')[0],
                purchase_cost: '',
                salvage_value: '0.00',
                accumulated_depreciation: '0.00',
                useful_life_months: '',
                depreciation_method: 'STRAIGHT_LINE'
            });
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to register fixed asset.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2 text-indigo-600" /> Preparing Asset Register...</div>;

    return (
        <Card className="p-6 border-zinc-200/60 shadow-sm bg-white">
            <div className="border-b border-zinc-100 pb-3 mb-6">
                <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                    <Package className="text-indigo-600" size={20} /> Capital Asset Acquisition & Registration
                </h2>
                <p className="text-xs text-zinc-500">Capitalize physical assets into the asset sub-ledger with automatic schedule generation.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                        name="organization_id"
                        label="Organization Target"
                        value={formObject.organization_id}
                        onChange={handleInputChange}
                        options={organizations}
                        placeholder="Select organization..."
                        required
                    />

                    {/* Category Selector + Pop-up Modal Button */}
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="text-xs font-medium text-zinc-700">Asset Category</label>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5"
                            >
                                <Plus size={12} /> Add / Seed Categories
                            </button>
                        </div>
                        <SearchableSelectBox
                            options={categories}
                            value={formObject.assetcategory_id}
                            onChange={handleCategoryChange}
                            placeholder="Select asset category..."
                        />
                    </div>

                    <Select
                        name="store_id"
                        label="Assigned Branch / Plant"
                        value={formObject.store_id}
                        onChange={handleInputChange}
                        options={stores}
                        placeholder="Select plant location..."
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                        name="asset_number"
                        label="Asset Tag / Barcode #"
                        placeholder="e.g., FA-2026-0091"
                        value={formObject.asset_number}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        name="name"
                        label="Asset Title"
                        placeholder="e.g., CAT 500kW Industrial Generator"
                        value={formObject.name}
                        onChange={handleInputChange}
                        required
                    />
                    <SearchableSelectBox
                        label="Vendor / Supplier"
                        options={vendors}
                        value={formObject.vendor_id}
                        required
                        onChange={(e) => setFormObject(p => ({ ...p, vendor_id: e.target.value }))}
                        placeholder="Select supplier..."
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-zinc-50 p-4 rounded-lg border border-zinc-200">
                    <Input
                        name="purchase_cost"
                        label="Capitalized Cost"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formObject.purchase_cost}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        name="salvage_value"
                        label="Residual / Salvage Value"
                        type="number"
                        step="0.01"
                        value={formObject.salvage_value}
                        onChange={handleInputChange}
                    />
                    <Input
                        name="accumulated_depreciation"
                        label="Prior Accum. Depr. (Migration)"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formObject.accumulated_depreciation}
                        onChange={handleInputChange}
                    />
                    <Input
                        name="in_service_date"
                        label="In-Service Date"
                        type="date"
                        value={formObject.in_service_date}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        name="useful_life_months"
                        label="Useful Life (Months)"
                        type="number"
                        value={formObject.useful_life_months}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-zinc-700 mb-1">Asset Description & Technical Specs</label>
                    <textarea
                        name="description"
                        value={formObject.description}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:ring-2 focus:ring-indigo-600 focus:outline-none"
                        placeholder="Serial numbers, warranty terms, setup details..."
                    />
                </div>

                <div className="flex justify-end pt-4">
                    <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-6 py-2">
                        {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1 inline" />} Capitalize Asset
                    </Button>
                </div>
            </form>

            {/* Quick Category Add / Seed Modal */}
            <AssetCategoryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                organizationId={formObject.organization_id}
                currentUser={currentUser}
                onCategoryCreated={loadCategories}
            />
        </Card>
    );
};